/* LIBS */
import React, {
    FC, useEffect, useMemo, useState,
} from 'react';
import { useFragment } from '@apollo/client';

/* QUERIES */
import DesktopDish from 'app/components/DesktopDish';
import MobileDish from 'app/components/MobileDish';
import { useDishCustoms } from 'app/views/Hooks/useDishCustoms';
import { DishCardRegular } from 'app/components/BR1258/DishCardRegular/DishCardRegular';
import { DishCardSmall } from 'app/components/BR1258/DishCardSmall/DishCardSmall';
import MENU_DISH_FRAGMENT from './graphql/menuDishFragment.graphql';
import { useEextendedDishData } from '../hooks';


type DishCardLayouts = 'small' | 'mobile' | 'desktop' | 'selectionMobile'
interface IDishDAC {
    id: string,
    cardLayout: DishCardLayouts,
    cardProps: unknown,
    showTestCards?: boolean,
}

const CardLayoutMap: Record<DishCardLayouts, any> = {
    small: DishCardSmall,
    mobile: DishCardRegular,
    desktop: DesktopDish,
    selectionMobile: MobileDish,
};

const DishDAC: FC<IDishDAC> = ({
    id, cardLayout, cardProps, showTestCards = false,
}: IDishDAC) => {
    const { data } = useFragment<any>({
        fragment: MENU_DISH_FRAGMENT,
        from: {
            __ref: `menuDish:${id}`,
        },
    });

    const {
        id: dishId,
        title,
        titleEn,
        caption,
        captionEn,
        composition,
        cooking_time: cookingTime,
        cook_until,
        weight,
        price,
        promoPrice,
        portions,
        previewImage,
        previewMobileImage,
        verticalImage,
        umAlias,
        category_id: categoryId,
        tags,
        visibleStatus,
        nutrition,
        // Кастомизация из селекста в карточке диша
        secondDishPart,
        replaceableIngredient,
        additionalDescription,
        option_tags: optionTags,
        highlight_words: highlightWords,
        highlight_description: highlightDescription,
        highlight_description_title: highlightDescriptionTitle,
        highlight_image: highlightImage,
        previewMobileAlterImage,
    } = data;

    const {
        ingredientsList, upsellDishesList,
        isDishCardWithCustomSelect, isDishCardWithCustomTabBar, isDishWithAdditionalDishesSelect,
    } = useDishCustoms(dishId || '');

    const isRootIdEqualsSelectedId = useMemo(
        () => isDishWithAdditionalDishesSelect && !isDishCardWithCustomSelect && upsellDishesList?.length,
        [isDishWithAdditionalDishesSelect, isDishCardWithCustomSelect, upsellDishesList],
    );

    const initValueOfSelectedDishId = useMemo(
        () => {
            if (isDishCardWithCustomSelect || isDishCardWithCustomTabBar) {
                return dishId;
            }
            const optionFromSession = sessionStorage.getItem(`${dishId}:selectedDishId`);
            if (optionFromSession && JSON.parse(optionFromSession) && isDishWithAdditionalDishesSelect) {
                return optionFromSession;
            }
            if (isDishWithAdditionalDishesSelect && upsellDishesList?.length) {
                return upsellDishesList[0].id;
            }
            return dishId;
        },
        [
            isDishWithAdditionalDishesSelect, isDishCardWithCustomSelect, isDishCardWithCustomTabBar,
            upsellDishesList, dishId,
        ],
    );
    const [selectedDishId, setSelectedDishId] = useState(initValueOfSelectedDishId);

    const getEextendedDishData = useEextendedDishData();

    /* SELECTORS */
    const DishComponent = useMemo(
        () => (
            CardLayoutMap[cardLayout]
        ),
        [cardLayout],
    );

    const selectedDishPortions = useMemo(
        () => upsellDishesList.find((d: any) => d.id === selectedDishId)?.portions || null,
        [selectedDishId, upsellDishesList],
    );

    const onNextIdSelected = (nextId: string) => {
        setSelectedDishId(nextId);
    };

    useEffect(
        () => {
            if (isDishWithAdditionalDishesSelect && !isDishCardWithCustomSelect && !isDishCardWithCustomTabBar) {
                sessionStorage.setItem(`${dishId}:selectedDishId`, selectedDishId);
            }
        },
        [
            selectedDishId, dishId,
            isDishWithAdditionalDishesSelect, isDishCardWithCustomSelect, isDishCardWithCustomTabBar,
        ],
    );

    return (
        <DishComponent
            rootDishID={dishId}
            id={isRootIdEqualsSelectedId ? dishId : selectedDishId}
            title={title}
            titleEn={titleEn}
            caption={caption}
            captionEn={captionEn}
            composition={composition}
            cooking_time={cookingTime}
            cook_until={cook_until}
            weight={weight}
            price={price}
            promoPrice={promoPrice}
            portions={portions}
            previewImage={previewImage}
            previewMobileImage={previewMobileImage}
            verticalImage={verticalImage}
            previewMobileAlterImage={previewMobileAlterImage}
            umAlias={umAlias}
            categoryId={categoryId}
            secondDishPart={secondDishPart}
            replaceableIngredient={replaceableIngredient}
            tags={tags}
            visibleStatus={visibleStatus}
            nutrition={nutrition}
            // Кастомизация
            isDishCardWithCustomSelect={isDishCardWithCustomSelect}
            isDishCardWithCustomTabBar={isDishCardWithCustomTabBar}
            isDishWithAdditionalDishesSelect={isDishWithAdditionalDishesSelect}
            selectedDishId={selectedDishId}
            selectedDishPortions={selectedDishPortions}
            onNextIdSelected={onNextIdSelected}
            getEextendedDishData={getEextendedDishData}
            ingredientsList={ingredientsList}
            upsellDishesList={upsellDishesList}
            optionTags={optionTags}
            additionalDescription={additionalDescription}
            highlightWords={highlightWords}
            highlightDescription={highlightDescription}
            highlightDescriptionTitle={highlightDescriptionTitle}
            highlightImage={highlightImage}
            {...cardProps as Object}
        />
    );
};

export default React.memo(DishDAC);
